import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { coachmarks } from '@models/coachmark.class';
import { IContract } from '@models/contract.class';
import { LoggedInUser } from '@models/logged-in-user.class';
import { TimeWeightedReturn } from '@models/time-weighted-return.class';
import { Store } from '@ngrx/store';
import { isEmpty, isEqual } from 'lodash';
import {
  combineLatest as observableCombineLatest,
  empty as observableEmpty,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  AppState,
  DW_ACCOUNT_STATUS,
  GEOLOCATION_NOTIFICATION_MESSAGE,
  LEGAL_PERSONALITY_TYPE,
  NO_SCROLL,
  OPEN_PRICE_ALERTS,
  POSITION_COLUMNS,
  SET_USER_PRIVACY,
  TIME_WEIGHTED_RETURN,
  USER_ROLES,
} from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import {
  selectCurrentContract,
  selectTotals,
} from 'src/app/state/selectors/current-contract.selectors';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { CoachmarkService } from 'src/components/common/coachmark/coachmark.service';
import { SupportCode } from 'src/components/common/navigation/support-code.class';
import { NewRelicLogger } from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { SideModalComponent } from 'src/components/side-modal/side-modal.component';
import {
  ContractsService,
  GeolocationService,
  NotificationService,
  SessionService,
  UserService,
} from 'src/core';

interface IContractNavigation extends IContract {
  comission: string;
  rating: string;
}

interface IInfoItem {
  label: string;
  value: string;
  class: string;
  visibleUsa: boolean;
  labelUsa?: string;
}

interface IMenuItem {
  url: string;
  name: string;
  type: string;
  clientType: number;
  active: boolean;
  menuClass?: string;
  submenu?: string;
  childs?: Array<Partial<IMenuItem>>;
  shouldHide?: boolean;
  showUsaContract?: boolean;
  tourAnchor?: string;
}

interface IHeaderContractInfo {
  buyingPower: IHeaderContractInfoItem;
  sellingPower: IHeaderContractInfoItem;
  margin: IHeaderContractInfoItem;
  marketValueSubTot: IHeaderContractInfoItem;
}

interface IHeaderContractInfoItem {
  visible: boolean;
  total: number;
}

interface IControls {
  showInfo: boolean;
  showTooltip: boolean;
}

@Component({
  selector: 'hb-navigation',
  templateUrl: 'navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NavigationComponent implements OnInit, OnDestroy {
  @ViewChildren('supportCode') set supportCodeListener(value: QueryList<any>) {
    const [supportCodeEl] = value.toArray();

    if (!this.supportCodeEl && supportCodeEl) {
      this.supportCodeEl = supportCodeEl;
      this.supportCodeEl.nativeElement.addEventListener('click', (event: any) =>
        this.handleSupportCodeRequest(event),
      );
    }
  }
  @Input()
  set hasDashboard(hasDashboard: boolean) {
    const rectTopWithDashboardLink = 30;
    this.initialNavigationRectTop = hasDashboard ? rectTopWithDashboardLink : 0;
  }
  @ViewChild('sideModal') sideModal: SideModalComponent;

  public isUsaContract: boolean;

  public serviceInterruption: boolean;

  public geolocationNotificationMessage = GEOLOCATION_NOTIFICATION_MESSAGE;
  public tradingTitle = 'Trading';

  public mainMenu: IMenuItem[] = [
    {
      url: '/main/portfolio',
      name: 'Portafolio',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: true,
      tourAnchor: 'portfolio-coachmark-navbar',
    },
    {
      url: '/main/mercados',
      name: 'Mercados',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: true,
      tourAnchor: 'markets-coachmark-navbar',
    },
    {
      url: '/main/news',
      name: 'Noticias',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: false,
      tourAnchor: 'news-coachmark-navbar',
    },
    {
      url: '/main/funds',
      name: 'Fondos',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: false,
      tourAnchor: 'funds-coachmark-navbar',
    },
    {
      url: '/main/ordenes',
      name: 'Órdenes',
      type: 'dropdown',
      clientType: 1,
      active: false,
      showUsaContract: false,
      menuClass: '.orders-menu',
      submenu: 'orders-menu',
      tourAnchor: 'orders-coachmark-navbar',
      childs: [
        {
          url: '/main/ordenes',
          name: 'Órdenes intradía',
        },
        {
          url: '/main/ordenes/historial',
          name: 'Historial de transacciones',
        },
      ],
    },
    {
      url: '/main/ordenes/historial',
      name: 'Movimientos',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: true,
      menuClass: '.orders-menu',
      submenu: 'orders-menu',
      tourAnchor: 'movements-coachmark-navbar',
    },
    {
      url: '/main/herramientas',
      name: 'Herramientas',
      type: 'dropdown',
      clientType: 1,
      active: false,
      showUsaContract: false,
      menuClass: '.tools-menu',
      submenu: 'tools-menu',
      tourAnchor: 'tools-coachmark-navbar',
      childs: [
        {
          url: '/main/herramientas/rendimientos',
          name: 'Rendimientos',
        },
        {
          url: '/main/herramientas/aforos',
          name: 'Aforos',
        },
      ],
    },
    {
      url: '/main/herramientas/rendimientos',
      name: 'Rendimientos',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: true,
      menuClass: '.tools-menu',
      tourAnchor: 'performance-coachmark-navbar',
    },
    {
      url: '/main/documentos',
      name: 'Documentos',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: false,
      tourAnchor: 'documents-coachmark-navbar',
    },
    {
      url: '/main/cuenta-bancaria',
      name: 'Cuenta bancaria',
      type: 'link',
      clientType: 1,
      active: false,
      showUsaContract: false,
      tourAnchor: 'bank-accounts-coachmark-navbar',
    },
  ];
  public scrolled: any = false;
  public isVisibleMenu = false;

  public shouldShowGeolocationNoticeBar$: Observable<boolean>;

  public headerContractInfo: IHeaderContractInfo = {
    buyingPower: { total: 0, visible: true },
    sellingPower: { total: 0, visible: true },
    margin: { total: 0, visible: true },
    marketValueSubTot: { total: 0, visible: true },
  };
  public withGraph = false;
  public orientation = 'horizontal';
  public controls: IControls = {
    showInfo: true,
    showTooltip: false,
  };

  public contractInfoItems: IInfoItem[] = [
    {
      label: 'Poder de compra',
      value: 'buyingPower',
      class: 'buying',
      visibleUsa: true,
      labelUsa: 'Disponible para comprar',
    },
    {
      label: 'Valor del portafolio',
      value: 'marketValueSubTot',
      class: 'subtotal',
      visibleUsa: true,
      labelUsa: 'Valor del portafolio',
    },
  ];
  public canSearchContracts = false;
  public isReadOnlySession = false;
  public isAdvisor: boolean = undefined;
  public isLoremAccount = false;
  public thematicStrategy = false;

  public user: LoggedInUser;
  public currentContract: IContractNavigation;
  public elPatronUrl: any = '';
  public skeleton = true;
  public showSmallIcon: boolean;
  public enablePriceAlerts$: Observable<boolean>;

  public support = {
    code: '',
  };

  public isSupportCodeVisible = false;
  public showEditStrategyControl: boolean;

  public isMoreOptionsMenuOpen = false;
  public itemMenuWithCoachmark = 'Noticias';

  private subscriptions = new Subscription();
  private storeTotalsSubscription: Subscription;
  private enableNewsFeature = false;
  private isUserGlobal = false;
  private isInteralUser = false;
  private userHasShortSell = false;
  private loggedInUser$: Observable<any>;
  private contracts$: Observable<any[]>;
  private isFromLorem = false;
  private userCanUseAccountManager = false;
  private contractCanUseAccountManager = false;
  private isReadOnly = false;
  private supportCodeEl: ElementRef;
  private initialNavigationRectTop = 0;
  private activeRoute: string;
  private routeActivatedCoachmark = 'portfolio';

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private store: Store<AppState>,
    private el: ElementRef,
    private notificationService: NotificationService,
    private contractsService: ContractsService,
    private userService: UserService,
    private newRelicLogger: NewRelicLogger,
    private geolocationService: GeolocationService,
    private readonly route: ActivatedRoute,
    private readonly coachmarkService: CoachmarkService,
  ) {}

  ngOnInit() {
    /**
     * Initial activeRoute asignation in first load
     */
    this.activeRoute =
      this.route.snapshot.firstChild &&
      this.route.snapshot.firstChild.url[
        this.route.snapshot.firstChild.url.length - 1
      ].path;
    this.initRouteSubscription();

    this.el.nativeElement.classList.add('navigation');
    this.loggedInUser$ = this.store
      .select('loggedInUser')
      .pipe(map((values: any) => values.toJS()));
    this.contracts$ = this.store
      .select('contracts')
      .pipe(map((values: any) => values.toJS()));

    this.subscriptions.add(
      this.loggedInUser$.subscribe((values: LoggedInUser) => {
        this.user = values;

        if (this.user.privacy) {
          this.onNotifyShowInfo(!this.user.privacy);
        }
      }),
    );

    this.subscriptions.add(
      this.store
        .select('serviceInterruption')
        .pipe(
          filter((state) => state !== undefined),
          map((e: any) => e.toJS()),
        )
        .subscribe(
          ({
            position,
            buyingPower,
          }: {
            position: boolean;
            buyingPower: boolean;
          }) => {
            this.headerContractInfo.buyingPower.visible = !buyingPower;
            this.headerContractInfo.marketValueSubTot.visible = !position;
            this.headerContractInfo.margin.visible = !position;
            this.headerContractInfo.sellingPower.visible = !position;
          },
        ),
    );

    const nextSearch$ = new Subject();

    this.subscriptions.add(
      this.store
        .select(selectCurrentContract)
        .pipe(
          filter((currentContract) => !isEmpty(currentContract)),
          distinctUntilChanged((a, b) => {
            const isSameContract = a.contractID === b.contractID;
            const isSameLoremAccount = a.isLoremAccount === b.isLoremAccount;
            return isSameContract && isSameLoremAccount;
          }),
          map((currentContract) => {
            const contractChanged: boolean =
              !this.currentContract ||
              this.currentContract.contractID !== currentContract.contractID;
            return [currentContract, contractChanged];
          }),
          tap(([currentContract, contractChanged]: [IContract, boolean]) => {
            if (contractChanged) {
              nextSearch$.next(currentContract.contractID);
              this.skeleton = true;
            }
          }),
        )
        .subscribe(
          ([currentContract, contractChanged]: [IContract, boolean]) => {
            this.currentContract = currentContract as IContractNavigation;
            this.isReadOnlySession = currentContract.isDiscretional;
            this.userHasShortSell = currentContract.tradeShortSell;
            this.thematicStrategy = currentContract.isDiscretional;
            this.isLoremAccount = this.currentContract.isLoremAccount;
            this.isUsaContract = currentContract.isUsa;
            this.tradingTitle = this.isUsaContract ? 'Trading USA' : 'Trading';
            this.contractCanUseAccountManager =
              this.currentContract.legalPersonalityType ===
                LEGAL_PERSONALITY_TYPE.PHYSICAL_PERSON &&
              !this.isReadOnlySession;
            this.currentContract.clientName =
              (this.currentContract.clientName &&
                this.currentContract.clientName.replace(/[|]/g, ' ')) ||
              '';

            if (contractChanged) {
              this.isUsaContract &&
                this.scanTradingGlobalStatus(
                  this.currentContract.loremAccountId,
                );

              this.contractInfoItems.forEach(
                ({ value }) =>
                  (this.headerContractInfo[
                    value as keyof IHeaderContractInfo
                  ].total = 0),
              );

              // Add independient subscribe to avoid overloop error by subscriptions.add into subscriptions.add
              this.storeTotalsSubscription = this.store
                .select(selectTotals)
                .pipe(
                  takeUntil(nextSearch$),
                  filter((totals) => !!totals),
                  distinctUntilChanged((a, b) => isEqual(a, b)),
                  flatMap((totalsValue) =>
                    this.contractsService
                      .getTimeWeightedReturn(currentContract.contractID)
                      .pipe(
                        distinctUntilChanged(),
                        tap((twr: TimeWeightedReturn) => {
                          const {
                            sellingPower,
                            margin,
                            buyingPower,
                            buyingPowerSecureAmount,
                          } = totalsValue;
                          this.headerContractInfo = {
                            sellingPower: {
                              ...this.headerContractInfo.sellingPower,
                              total: sellingPower,
                            },
                            margin: {
                              ...this.headerContractInfo.margin,
                              total: margin,
                            },
                            buyingPower: {
                              ...this.headerContractInfo.buyingPower,
                              total: this.isUsaContract
                                ? buyingPowerSecureAmount
                                : buyingPower,
                            },
                            marketValueSubTot: {
                              total:
                                twr && twr.todayPortfolioValue !== undefined
                                  ? twr.todayPortfolioValue
                                  : undefined,
                              visible:
                                twr && twr.todayPortfolioValue !== undefined,
                            },
                          };
                        }),
                      ),
                  ),
                )
                .subscribe((twrData) => {
                  this.skeleton = false;
                  this.store.dispatch({
                    type: TIME_WEIGHTED_RETURN,
                    payload: twrData,
                  });
                  this.initializeCoachmarks();
                });
              this.getPositionSummary(currentContract.contractID);
            }

            this.hideNavLinks(this.isInteralUser);
            this.userRating(currentContract);
            this.getHbIcon();
          },
        ),
    );

    this.scanForUserRole();
    this.scanForUserTypeContract();
    this.scanForModals();
    this.scanForElPatronData();
    this.scanForPriceAlerts();
    this.scanForNewsFeature();
    this.getUserAppConfiguration();
    this.validateUserIsLocked();
    this.checkShouldDisplayGeolocationNoticeBar();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.storeTotalsSubscription && this.storeTotalsSubscription.unsubscribe();
    this.contractsService.unsubscribePositionSummaryLoop();

    this.supportCodeEl &&
      this.supportCodeEl.nativeElement.removeEventListener(
        'click',
        this.handleSupportCodeRequest,
      );
    this.supportCodeEl = undefined;
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event: any) {
    const contractInfoRectTop = 74;
    const scrollY = contractInfoRectTop + this.initialNavigationRectTop;
    this.scrolled = window.scrollY > scrollY;
  }

  public sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  public userRating(contract: any) {
    let rating: string;
    const commissionBuy = parseFloat(contract.commissionBuy);

    if (commissionBuy === 0.25) {
      rating = 'Inversionista';
    } else if (commissionBuy === 0.2) {
      rating = 'Silver';
    } else if (commissionBuy === 0.15) {
      rating = 'Gold';
    } else if (commissionBuy === 0.125) {
      rating = 'Platinum';
    } else if (commissionBuy === 0.1) {
      rating = 'Black';
    } else {
      rating = 'Titanium';
    }

    this.currentContract.comission =
      commissionBuy === 0.125
        ? `${commissionBuy.toFixed(3)} %`
        : `${commissionBuy.toFixed(2)} %`;

    this.currentContract.rating = rating;
  }

  public isUserFromLorem() {
    this.subscriptions.add(
      this.store
        .select('partyId')
        .pipe(filter((value: any) => value !== undefined))
        .subscribe((value: any) => {
          this.isFromLorem = value === 'PartyNotFound' ? false : true;
          this.hideNavLinks(this.isUserGlobal);
        }),
    );
  }

  public scanForUserTypeContract() {
    this.subscriptions.add(
      this.store.select('userGlobal').subscribe((value: any) => {
        this.isUserGlobal = value;
        this.isUserFromLorem();
      }),
    );
  }

  hideNavLinks(shouldHide: boolean = false) {
    const DOCUMENTS = 0;
    const AFOROS = 0;
    const LINKS_MENU = ['Documentos'];
    const LINKS_SUBMENU = ['Aforos'];
    const CUENTA_BANCARIA = 'Cuenta bancaria';
    const NEWS = 'Noticias';
    const MOVIMIENTOS = 'Movimientos';
    const RENDIMIENTOS = 'Rendimientos';

    this.mainMenu.map((link: any) => {
      link.shouldHide = false;
      if (link.name === LINKS_MENU[DOCUMENTS]) {
        link.shouldHide = shouldHide;

        if (this.isInteralUser || this.isUserGlobal) {
          link.shouldHide = true;
        }

        link.shouldHide = this.isReadOnly ? false : link.shouldHide;
      }
      if (link.childs !== undefined) {
        link.childs.map((childLink: any) => {
          childLink.shouldHide = false;

          if (childLink.name === LINKS_SUBMENU[AFOROS]) {
            childLink.shouldHide = !this.userHasShortSell;
          }
        });
      }
      if (link.name === CUENTA_BANCARIA) {
        if (featureFlags.isAccountManagerEnableToLegacy.isEnabled()) {
          link.shouldHide = !this.canUseAM();
        } else {
          link.shouldHide = !this.isLoremAccount;
        }
      }
      if (this.isUsaContract && !link.showUsaContract) {
        link.shouldHide = true;
      } else if (!this.isUsaContract) {
        if (link.name === MOVIMIENTOS || link.name === RENDIMIENTOS) {
          link.shouldHide = true;
        }
      }
      if (link.name === NEWS) {
        link.shouldHide = !this.enableNewsFeature || this.isUsaContract;
      }
    });
  }

  public scanForUserRole() {
    this.subscriptions.add(
      this.store
        .select('userRole')
        .pipe(filter((value: any) => value !== undefined))
        .subscribe((value: any) => {
          this.canSearchContracts =
            value === USER_ROLES.ADVISOR ||
            value === USER_ROLES.RI ||
            value === USER_ROLES.SUBDIRECTOR;
          this.isInteralUser =
            value === USER_ROLES.RI || value === USER_ROLES.SUBDIRECTOR;
          this.isAdvisor = value === USER_ROLES.ADVISOR;
          this.isReadOnly = value === USER_ROLES.READONLY;
          this.userCanUseAccountManager =
            !this.isInteralUser && !this.isAdvisor && !this.isReadOnly;
          this.hideNavLinks(this.isInteralUser);
        }),
    );
  }

  public scanForElPatronData() {
    this.subscriptions.add(
      this.store
        .select('elPatronData')
        .pipe(
          map((value: any) => value.toJS()),
          filter((value: any) => value.URL_SETTINGS !== undefined),
          take(1),
        )
        .subscribe((value: any) => {
          this.elPatronUrl = value;
        }),
    );
  }

  public getPositionSummary(contractId: string): void {
    this.subscriptions.add(
      this.contractsService
        .getPositionSummary(contractId, false)
        .pipe(
          catchError(() => {
            return observableEmpty();
          }),
          take(1),
        )
        .subscribe(),
    );
  }

  public logout(): void {
    this.sessionService.logout();
  }

  public toggleMenu($event: any, isVisible: boolean, menu: string) {
    if ($event.target !== undefined) {
      this.isVisibleMenu = $event.type === 'click' && !isVisible;
      const classMenu = document.querySelector(menu);
      if (!this.isVisibleMenu) {
        (classMenu as HTMLElement).style.opacity = '0';
        (classMenu as HTMLElement).style.display = 'none';
        (classMenu as HTMLElement).style.zIndex = '102';
      } else {
        (classMenu as HTMLElement).style.opacity = '1';
        (classMenu as HTMLElement).style.display = 'block';
        (classMenu as HTMLElement).style.zIndex = '105';
      }
    }
  }

  public toggleMenuForceClose(menu: string, event: any): void {
    event.stopPropagation();
    const classMenu = document.querySelector(menu);
    if (classMenu) {
      (classMenu as HTMLElement).style.opacity = '0';
      (classMenu as HTMLElement).style.display = 'none';
    }
  }

  public toggleMenuContract(event: any) {
    this.setSupportCodeVisibility(false);

    if (event.target === undefined) {
      return;
    }
    const source = event.target;
    const classElements = source.classList.value;
    event.stopPropagation();
    const classMenu = document.querySelector('.contracts-combo');
    if (classMenu) {
      if (classElements.indexOf('contracts-combo-item active') !== -1) {
        (classMenu as HTMLElement).style.opacity = '0';
        (classMenu as HTMLElement).style.display = 'none';
      } else {
        (classMenu as HTMLElement).style.opacity = '1';
        (classMenu as HTMLElement).style.display = 'block';
      }
    }
  }

  public toggleUserPrivacy() {
    this.controls.showInfo = !this.controls.showInfo;
    this.store.dispatch({
      type: SET_USER_PRIVACY,
      payload: !this.controls.showInfo,
    });
  }

  public handleMoreOptionsClick(): void {
    this.isMoreOptionsMenuOpen = !this.isMoreOptionsMenuOpen;
  }

  public handleAlertsClick() {
    this.updateNavigationClass(true);
    this.store.dispatch({
      type: OPEN_PRICE_ALERTS,
      payload: true,
    });
  }

  public handleEditStrategy() {
    this.toggleEditStrategy();
    this.handleMoreOptionsClick();
  }

  public toggleEditStrategy() {
    this.showEditStrategyControl = !this.showEditStrategyControl;
    this.store.dispatch({
      type: NO_SCROLL,
      payload: { block: this.showEditStrategyControl, source: 'EditStrategy' },
    });
  }

  public onNotifyShowInfo(isShown: boolean) {
    this.controls.showInfo = isShown;
  }

  public setSupportCodeVisibility(showSupportCode: boolean) {
    this.isSupportCodeVisible = showSupportCode;
  }

  public onGeolocationLearnMore(): void {
    this.geolocationService.setShowNotice();
  }

  public handleBuyingPowerSecureAmount(item: any): void {
    if (this.isUsaContract && item.value === 'buyingPower') {
      this.sideModal.showModal();
    }
  }

  public handleCloseSideModal(): void {
    this.sideModal.handleHide();
  }

  private scanTradingGlobalStatus(accountId) {
    this.subscriptions.add(
      this.contractsService
        .getTradingGlobalAccount(accountId)
        .subscribe((account: any) => {
          this.store.dispatch({
            type: DW_ACCOUNT_STATUS,
            payload: account.dw_status,
          });
        }),
    );
  }

  private initRouteSubscription(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map((event: any) => this.splitUrl(event.url)),
          distinctUntilChanged(),
        )
        .subscribe((activeRoute: string) => {
          this.activeRoute = activeRoute;
        }),
    );
  }

  private initializeCoachmarks(): void {
    if (this.activeRoute && this.activeRoute === this.routeActivatedCoachmark) {
      if (
        !this.headerContractInfo.buyingPower.total &&
        !this.headerContractInfo.marketValueSubTot.total
      ) {
        this.coachmarkService.initializeTour(
          this.isUsaContract
            ? coachmarks.onboarding.usa
            : coachmarks.onboarding.mx,
        );
      }
    }
  }

  private splitUrl(url: string): string {
    const splitted: string[] = url ? url.split('/') : [];
    return splitted.length > 0 ? splitted[2] : undefined;
  }

  private scanForModals() {
    this.subscriptions.add(
      this.store
        .select('operation')
        .pipe(
          map((value: any) => value.toJS()),
          filter((value: any) => value.isModalOpened !== undefined),
        )
        .subscribe((value: any) => {
          this.updateNavigationClass(value.isModalOpened);
        }),
    );
  }

  private updateNavigationClass(setClass: boolean) {
    const header: any = document.getElementsByClassName('nav-secondary');

    if (header !== undefined && header.length > 0) {
      if (setClass) {
        header[0].classList.add('hide-from-modal');
      } else {
        header[0].classList.remove('hide-from-modal');
      }
    }
  }

  private getUserAppConfiguration(): void {
    this.subscriptions.add(
      this.userService
        .getUserAppConfiguration()
        .pipe(take(1))
        .subscribe(
          (value: any) => {
            const { uiConfiguration } = value;
            if (uiConfiguration) {
              this.store.dispatch({
                type: POSITION_COLUMNS,
                payload:
                  uiConfiguration &&
                  JSON.stringify(JSON.parse(uiConfiguration).chosenColumns),
              });
            }
          },
          (error) => console.error(error),
        ),
    );
  }

  private validateUserIsLocked() {
    const isLockedId = 5;
    const contractsLength = 1;

    this.subscriptions.add(
      observableCombineLatest(
        this.loggedInUser$.pipe(
          filter((user: any) => user.statusId === isLockedId),
        ),
        this.contracts$.pipe(
          filter((contrats: any[]) => contrats.length === contractsLength),
        ),
      ).subscribe((data) => {
        this.router.navigate(['user-is-locked']);
      }),
    );
  }

  private handleSupportCodeRequest({ detail }: MouseEvent) {
    const clicksToOpenDialogSupport = 3;
    if (detail === clicksToOpenDialogSupport && this.currentContract) {
      const supportCode = new SupportCode(
        this.isFromLorem,
        this.currentContract.isLoremAccount,
        this.currentContract.isDiscretional,
        this.user.hasLevel2,
        this.currentContract.contractID,
        this.currentContract.isReadOnly,
        this.currentContract.isReadOnlyByRelationship,
      );
      this.support.code = supportCode.getCode();
      this.setSupportCodeVisibility(true);
      this.newRelicLogger.setEvent(
        'SUPPORT_CODE',
        supportCode.getTrackingObject(),
      );
    }
  }

  private getHbIcon() {
    if (featureFlags.isAccountManagerEnableToLegacy.isEnabled()) {
      this.showSmallIcon = this.canUseAM();
    } else {
      this.showSmallIcon = this.isLoremAccount;
    }
  }

  private canUseAM(): boolean {
    return this.userCanUseAccountManager && this.contractCanUseAccountManager;
  }

  private checkShouldDisplayGeolocationNoticeBar() {
    this.shouldShowGeolocationNoticeBar$ =
      this.geolocationService.shouldRestricGeolocationRelatedOperations$;
  }

  private scanForPriceAlerts() {
    this.enablePriceAlerts$ = this.store
      .select(selectFeatureFlag('priceAlerts'))
      .pipe(map(Boolean));
  }

  private scanForNewsFeature() {
    this.subscriptions.add(
      this.store
        .select(selectFeatureFlag('news'))
        .pipe(
          filter(
            (enableNewsFeature: boolean) => enableNewsFeature !== undefined,
          ),
        )
        .subscribe((enableNewsFeature: boolean) => {
          this.enableNewsFeature = enableNewsFeature;
          this.hideNavLinks();
        }),
    );
  }
}
